/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import {
    Depths,
    DirectionalHint,
    getTheme,
    IIconProps,
    IStackStyles,
    NeutralColors,
    PrimaryButton,
    Stack,
    TooltipHost
} from "@fluentui/react";
import PreviewActions from "./PreviewActions";
import ViewSectionTitle from "./ViewSectionTitle";
import {
    EvaluationStrategy,
    ISettingGroupDefinition,
    ISettingValueEx,
    SettingLevel
} from "@nuance/hierarchical-settings-ui-domain";
import { Pivot, PivotItem, Separator } from "@fluentui/react";
import {
    PreviewComponentCommands,
    IPreviewComponentState,
    SettingLevelToUID
} from "./PreviewComponentViewModel";
import { IMainViewViewModel } from "./MainViewViewModel";
import {
    SaveChangedValuesCallback,
    HierarchicalSettingsComponent,
    SettingDefinitionHasAvailableControls,
    NoSettingsAvailable
} from "@nuance/hap-components";

const previewComponentCommands = new PreviewComponentCommands();
const cmds = previewComponentCommands.Cmds;

/**
 * Preview Component properties
 */
export interface IPreviewComponentProps {
    settingDefinition: ISettingGroupDefinition;
    mainViewVM: IMainViewViewModel;
}

const saveIcon: IIconProps = { iconName: "Save" };

const stackSaveSettingsStyles: IStackStyles = {
    root: {
        marginBottom: 16
    }
};

/**
 * PreviewComponent
 * @param props Preview component properties
 * @returns Preview component rendering function
 */
const PreviewComponent = (props: IPreviewComponentProps): JSX.Element => {
    const initialCacheSettingValues = {
        [SettingLevel.Organization]: PreviewComponentCommands.DefaultSettingValues(
            props.settingDefinition,
            SettingLevel.Organization
        ),
        [SettingLevel.Site]: undefined,
        [SettingLevel.Group]: undefined,
        [SettingLevel.User]: undefined,
        [SettingLevel.Product]: undefined,
        [SettingLevel.System]: undefined
    };

    const initialState: IPreviewComponentState = {
        currentLevel: SettingLevel.Organization,
        currentSettingValues: initialCacheSettingValues[SettingLevel.Organization],
        cachedSettingValues: initialCacheSettingValues,
        language: "en-US",
        canSave: false
    };

    const theme = getTheme();

    /**
     * Reference that tracks the changed setting values from the child
     */
    const changedSettingsRef = React.useRef<ISettingValueEx[]>([]);

    const [state, dispatch] = React.useReducer(previewComponentCommands.Reducer, initialState);

    const onSettingLevelChange = (item?: PivotItem) => {
        if (item) {
            const selectedLevel = item.props.itemKey as keyof typeof SettingLevel;
            dispatch(
                cmds.currentLevelChanged({
                    settingDefinition: props.settingDefinition,
                    settingLevel: SettingLevel[selectedLevel]
                })
            );
        }
    };

    const saveChangedSettings: SaveChangedValuesCallback = (
        changes: ISettingValueEx[],
        isValid: boolean
    ) => {
        const canSave = changes.length > 0 && isValid;
        if (canSave !== state.canSave) {
            dispatch(cmds.canSaveChanged(canSave));
        }
        changedSettingsRef.current = changes;
    };

    const evaluationStrategy = props.settingDefinition.EvaluationStrategy;

    const instance = React.useMemo(() => {
        return `${Math.random()}-${state.currentLevel}`;
    }, [state.currentSettingValues, props.settingDefinition, state.currentLevel]);

    React.useEffect(() => {
        dispatch(cmds.resetCachedSettings(props.settingDefinition));
    }, [props.settingDefinition]);

    const settingsAvailable = SettingDefinitionHasAvailableControls(
        props.settingDefinition,
        state.currentLevel
    );

    const noSettingsAvailableErrorMsg =
        props.settingDefinition.Fields.length == 0
            ? "Settings need to be added to the definition."
            : "No settings are available for this level.";

    return (
        <div style={{ background: NeutralColors.gray10, padding: "16px 24px" }}>
            <Stack>
                <Stack horizontal horizontalAlign="space-between">
                    <ViewSectionTitle text="Preview" />
                    <PreviewActions
                        changesMade={
                            JSON.stringify(initialCacheSettingValues[SettingLevel.Organization]) !==
                            JSON.stringify(state.currentSettingValues)
                        }
                        language={state.language}
                        onLanguageChange={language => {
                            dispatch(cmds.currentLanguageChanged(language));
                        }}
                        onReset={() => {
                            dispatch(cmds.resetCachedSettings(props.settingDefinition));
                        }}
                    />
                </Stack>
                <Pivot
                    aria-label="Org, site, group, user tab strip"
                    onLinkClick={onSettingLevelChange}
                    selectedKey={String(SettingLevel[state.currentLevel])}
                >
                    <PivotItem
                        headerText="Organization"
                        itemKey={SettingLevel[SettingLevel.Organization]}
                    />
                    {evaluationStrategy !== EvaluationStrategy.OrgGroups && (
                        <PivotItem headerText="Site" itemKey={SettingLevel[SettingLevel.Site]} />
                    )}
                    {evaluationStrategy !== EvaluationStrategy.OrgGroups && (
                        <PivotItem headerText="Group" itemKey={SettingLevel[SettingLevel.Group]} />
                    )}
                    {evaluationStrategy === EvaluationStrategy.OrgGroups && (
                        <PivotItem
                            headerText="Organization Group"
                            itemKey={SettingLevel[SettingLevel.Group]}
                        />
                    )}
                    <PivotItem headerText="User" itemKey={SettingLevel[SettingLevel.User]} />
                    <PivotItem headerText="System" itemKey={SettingLevel[SettingLevel.System]} />
                </Pivot>
                <Separator />
                {!settingsAvailable && (
                    <NoSettingsAvailable
                        noSettingsAvailableErrorMsg={noSettingsAvailableErrorMsg}
                    />
                )}
                {settingsAvailable && (
                    <>
                        <Stack horizontal styles={stackSaveSettingsStyles}>
                            <TooltipHost
                                content="Saves settings for this level."
                                directionalHint={DirectionalHint.rightCenter}
                            >
                                <PrimaryButton
                                    iconProps={saveIcon}
                                    disabled={!state.canSave}
                                    onClick={() => {
                                        dispatch(
                                            cmds.saveCurrentSettings(changedSettingsRef.current)
                                        );
                                    }}
                                >
                                    Save settings
                                </PrimaryButton>
                            </TooltipHost>
                        </Stack>
                        <div
                            style={{
                                boxShadow: Depths.depth8,
                                background: theme.semanticColors.bodyBackground,
                                padding: 8
                            }}
                        >
                            <HierarchicalSettingsComponent
                                key={instance}
                                settingDefinition={props.settingDefinition}
                                settingValues={state.currentSettingValues}
                                language={state.language}
                                currentLevel={state.currentLevel}
                                ownerName={PreviewComponentCommands.GetFakeName(state.currentLevel)}
                                ownerUID={SettingLevelToUID[state.currentLevel]}
                                onSettingValuesChanged={(changes, isValid) =>
                                    saveChangedSettings(changes, isValid)
                                }
                                isReadOnly={false}
                            />
                        </div>
                    </>
                )}
            </Stack>
        </div>
    );
};

export default PreviewComponent;
